import { useRef, useState } from "react";
import {
  VIDEO_CONSTRAINTS,
  AUDIO_CONSTRAINTS,
} from "../constants/interviewInstructions";

// Deduplicate devices by deviceId.
const deduplicateDevices = (devices) => {
  if (!Array.isArray(devices)) return [];

  const seen = new Set();
  return devices.filter((device) => {
    if (!device || !device.deviceId || seen.has(device.deviceId)) {
      return false;
    }
    seen.add(device.deviceId);
    return true;
  });
};

const stopMediaStream = (mediaStream) => {
  if (mediaStream && mediaStream.getTracks) {
    mediaStream.getTracks().forEach((track) => track.stop());
  }
};

// Manages camera/mic enumeration, permissions, and getUserMedia stream lifecycle.
export const useMediaDevices = ({ setCameraStatus, setErrorMessage }) => {
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [availableCameras, setAvailableCameras] = useState([]);
  const [availableMics, setAvailableMics] = useState([]);
  const [stream, setStream] = useState(null);
  const [permissionsStatus, setPermissionsStatus] = useState({
    camera: "unknown",
    microphone: "unknown",
  });
  const [isRequestingCamera, setIsRequestingCamera] = useState(false);
  const [isRequestingMic, setIsRequestingMic] = useState(false);
  const [hasRequestedPermissions, setHasRequestedPermissions] = useState(false);
  const [noCameraDetected, setNoCameraDetected] = useState(false);

  const activeStreamsRef = useRef({ audio: null, video: null });

  const stopAllStreams = () => {
    console.log("CLEANUP - Stopping all streams");
    if (activeStreamsRef.current.audio) {
      stopMediaStream(activeStreamsRef.current.audio);
      activeStreamsRef.current.audio = null;
    }
    if (activeStreamsRef.current.video) {
      stopMediaStream(activeStreamsRef.current.video);
      activeStreamsRef.current.video = null;
    }
  };

  const getOrCreateStream = async (type, deviceId = null) => {
    console.log(`STREAM MANAGER - Request for ${type} stream`, { deviceId });

    try {
      const existingStream = activeStreamsRef.current[type];

      // Check if we can reuse existing stream
      if (
        existingStream &&
        existingStream.getTracks().length > 0 &&
        existingStream.active
      ) {
        // Get current device ID from the stream
        const currentTrack =
          type === "audio"
            ? existingStream.getAudioTracks()[0]
            : existingStream.getVideoTracks()[0];
        const currentDevice = currentTrack?.getSettings().deviceId;

        // CRITICAL: If deviceId is null (browser default) OR if it matches current device, reuse
        // BUT: Always create fresh stream for audio when deviceId is null to detect browser changes
        if (type === "video" && (!deviceId || currentDevice === deviceId)) {
          console.log("STREAM MANAGER - Reusing existing video stream");
          return existingStream;
        } else if (type === "audio" && deviceId && currentDevice === deviceId) {
          // Only reuse audio if specific device was requested
          console.log(
            "STREAM MANAGER - Reusing existing audio stream (specific device)"
          );
          return existingStream;
        } else if (type === "audio" && !deviceId) {
          // Always get fresh audio stream when no device specified (picks up browser changes)
          console.log(
            "STREAM MANAGER - Creating fresh audio stream (browser default)"
          );
          stopMediaStream(existingStream);
          activeStreamsRef.current[type] = null;
        } else {
          console.log("STREAM MANAGER - Device changed, recreating stream");
          stopMediaStream(existingStream);
          activeStreamsRef.current[type] = null;
        }
      }

      // Create new stream
      const constraints =
        type === "audio"
          ? {
              audio: {
                ...AUDIO_CONSTRAINTS,
                ...(deviceId && { deviceId: { exact: deviceId } }),
              },
            }
          : {
              video: {
                width: { ideal: VIDEO_CONSTRAINTS.WIDTH_IDEAL },
                height: { ideal: VIDEO_CONSTRAINTS.HEIGHT_IDEAL },
                ...(deviceId && { deviceId: { exact: deviceId } }),
              },
            };

      console.log(`STREAM MANAGER - Creating new ${type} stream`);
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);

      activeStreamsRef.current[type] = newStream;
      console.log(`STREAM MANAGER - ${type} stream created and stored`);

      return newStream;
    } catch (error) {
      console.error(`STREAM MANAGER - Error getting ${type} stream:`, error);
      throw error;
    }
  };

  const checkAndEnumerateDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === "videoinput");
      const microphones = devices.filter(
        (device) => device.kind === "audioinput"
      );

      // Deduplicate devices to prevent duplicates in dropdown
      const uniqueCameras = deduplicateDevices(cameras);
      const uniqueMicrophones = deduplicateDevices(microphones);

      console.log(
        `DEVICE ENUM - Found ${cameras.length} cameras (${uniqueCameras.length} unique), ${microphones.length} microphones (${uniqueMicrophones.length} unique)`
      );

      setAvailableCameras(uniqueCameras);
      setAvailableMics(uniqueMicrophones);

      const cameraHasLabels = uniqueCameras.some(
        (device) => device.label !== ""
      );

      if (uniqueCameras.length === 0 && cameraHasLabels) {
        setNoCameraDetected(true);
        setCameraStatus("failed");
        setErrorMessage(
          "No camera detected. Please connect a camera and try again."
        );
        return {
          camera: "denied",
          microphone: uniqueMicrophones.length > 0 ? "granted" : "denied",
        };
      } else {
        setNoCameraDetected(false);
      }

      const micHasLabels = uniqueMicrophones.some(
        (device) => device.label !== ""
      );

      let permStatus = {
        camera: cameraHasLabels ? "granted" : "denied",
        microphone: micHasLabels ? "granted" : "denied",
      };

      if (navigator.permissions) {
        try {
          const cameraPermission = await navigator.permissions.query({
            name: "camera",
          });
          const micPermission = await navigator.permissions.query({
            name: "microphone",
          });

          permStatus.camera = cameraPermission.state;
          permStatus.microphone = micPermission.state;

          cameraPermission.onchange = () => checkAndEnumerateDevices();
          micPermission.onchange = () => checkAndEnumerateDevices();
        } catch (err) {
          console.warn("Permission query failed:", err);
        }
      }

      setPermissionsStatus(permStatus);

      // Auto-select default microphone on first load
      if (micHasLabels && uniqueMicrophones.length > 0 && !selectedMic) {
        // Use 'default' device or first available
        const defaultMic =
          uniqueMicrophones.find((m) => m.deviceId === "default") ||
          uniqueMicrophones[0];
        setSelectedMic(defaultMic.deviceId);
      }
      if (cameraHasLabels && uniqueCameras.length > 0 && !selectedCamera) {
        setSelectedCamera(uniqueCameras[0].deviceId);
      }

      return permStatus;
    } catch (error) {
      console.error("Error enumerating devices:", error);
      setPermissionsStatus({ camera: "denied", microphone: "denied" });
      setNoCameraDetected(true);
      setErrorMessage(
        "Error accessing devices. Please check your camera and try again."
      );
      return { camera: "denied", microphone: "denied" };
    }
  };

  const requestPermissions = async () => {
    if (hasRequestedPermissions) return;
    setHasRequestedPermissions(true);
    await checkAndEnumerateDevices();
  };

  return {
    selectedCamera,
    setSelectedCamera,
    selectedMic,
    setSelectedMic,
    availableCameras,
    availableMics,
    stream,
    setStream,
    permissionsStatus,
    setPermissionsStatus,
    isRequestingCamera,
    setIsRequestingCamera,
    isRequestingMic,
    setIsRequestingMic,
    hasRequestedPermissions,
    noCameraDetected,
    activeStreamsRef,
    stopMediaStream,
    stopAllStreams,
    getOrCreateStream,
    checkAndEnumerateDevices,
    requestPermissions,
  };
};
